import React from 'react';
import type { HumanReplyData } from '../../types/conciergeToolTypes';
import { useSupportCopy } from '../SupportThemeProvider';

/**
 * Assistant-like message bubble for a real human teammate's reply (Intercom
 * admin replied → webhook → SSE bridge). Distinct from the AI assistant
 * bubble: shows the agent's name + avatar + posted time.
 *
 * D-33 — colors come from the brand-controlled `--bc-support-*` CSS vars set
 * by `SupportThemeProvider`. The copy ("Support Team") and the brand-uploaded
 * fallback avatar URL come from `useSupportCopy()`.
 */
export function HumanReplyPart({ data }: { data: HumanReplyData | undefined }) {
  const { teamLabel, teamAvatarUrl } = useSupportCopy();

  if (!data?.body) return null;

  const initials = (data.adminName ?? 'Support').slice(0, 1).toUpperCase();
  // Provider-supplied avatar wins; brand-uploaded fallback is next; initials
  // are the last resort.
  const avatarUrl = data.adminAvatarUrl || teamAvatarUrl || '';

  return (
    <div className="tw:my-2 tw:flex tw:items-start tw:gap-2">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt=""
          className="tw:h-7 tw:w-7 tw:rounded-full tw:object-cover tw:bg-gray-100"
        />
      ) : (
        // D-33 — circle uses the soft-bg with a border ring instead of a
        // darker fill (collapsed `emerald-100` into `--bc-support-bg` + ring).
        <div
          className="tw:flex tw:h-7 tw:w-7 tw:items-center tw:justify-center tw:rounded-full tw:text-xs tw:font-semibold tw:ring-1"
          style={{
            backgroundColor: 'var(--bc-support-bg)',
            color: 'var(--bc-support-text)',
            // ring color via inline boxShadow because Tailwind ring-[var(...)]
            // has tricky precedence with the default ring offset utilities.
            boxShadow: 'inset 0 0 0 1px var(--bc-support-border)',
          }}
        >
          {initials}
        </div>
      )}
      <div className="tw:flex tw:flex-col tw:gap-0.5 tw:max-w-[80%]">
        <div className="tw:flex tw:items-center tw:gap-1.5 tw:text-xs">
          {data.adminName ? (
            <>
              <span className="tw:font-semibold tw:text-gray-800">{data.adminName}</span>
              <span aria-hidden="true" className="tw:text-gray-300">
                ·
              </span>
              <span className="tw:font-medium" style={{ color: 'var(--bc-support-text)' }}>
                {teamLabel}
              </span>
            </>
          ) : (
            <span className="tw:font-medium" style={{ color: 'var(--bc-support-text)' }}>
              {teamLabel}
            </span>
          )}
        </div>
        <div
          className="tw:rounded-2xl tw:rounded-tl-sm tw:px-3 tw:py-2 tw:text-sm tw:text-gray-900 tw:whitespace-pre-wrap tw:leading-relaxed"
          style={{ backgroundColor: 'var(--bc-support-bg)' }}
        >
          {stripHtml(data.body)}
        </div>
      </div>
    </div>
  );
}

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>(\n)?/gi, '\n')
    .replace(/<\/(p|div)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .trim();
}
